'use client';

import * as React from 'react';
import { toast } from 'react-hot-toast';
import { resendEmailVerification } from '@/lib/auth/authApi';
import styles from './Auth.module.css';

const RESEND_COOLDOWN_SEC = 60;

type EmailVerificationSentProps = {
  email: string;
  editableEmail?: boolean;
  initialCooldownSec?: number;
  message: React.ReactNode;
};

export function EmailVerificationSent({
  email,
  editableEmail = false,
  initialCooldownSec = 0,
  message,
}: EmailVerificationSentProps) {
  const [address, setAddress] = React.useState(email);
  const [cooldown, setCooldown] = React.useState(initialCooldownSec);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setAddress(email);
  }, [email]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((current) => current - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const onResend = async () => {
    const nextEmail = address.trim();
    if (!nextEmail) {
      toast.error('Укажите электронную почту');
      return;
    }
    setLoading(true);
    try {
      await resendEmailVerification(nextEmail);
      setCooldown(RESEND_COOLDOWN_SEC);
      toast.success('Если письмо ещё ждёт подтверждения, мы отправили новую ссылку');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Не удалось отправить ссылку');
    } finally {
      setLoading(false);
    }
  };

  const resendLabel =
    cooldown > 0
      ? `Отправить повторно через ${cooldown} с`
      : loading
        ? 'Отправляем…'
        : 'Отправить ссылку ещё раз';

  return (
    <div className={styles.actions}>
      <p className={styles.message}>{message}</p>
      {editableEmail ? (
        <label className={styles.field}>
          <span>Электронная почта</span>
          <input
            name="email"
            type="email"
            autoComplete="email"
            className={styles.input}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
          />
        </label>
      ) : null}
      <button
        type="button"
        className={styles.primaryButton}
        disabled={loading || cooldown > 0}
        onClick={() => void onResend()}
      >
        {resendLabel}
      </button>
    </div>
  );
}
